/**
 *
 *  User Profile Page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 06/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback } from "react";
import {
  NavLink,
  Route,
  Switch,
  // useHistory,
  useLocation,
  Link,
} from "react-router-dom";
import { Title, Meta } from "react-meta-elements";

import { useAppState } from "../../state";
import { useRegion } from "../../context/RegionProvider";

import moment from "moment"; // TODO: use dayjs instead

// Components
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import Home from "./Home/Home";
import Bookings from "./Bookings/Bookings";
import Payments from "./Payments/Payments";
import SessionDetails from "./SessionDetails/SessionDetails";
import Packages from "./Packages/Packages";
import Messages from "./Messages/Messages";
import SendReviewsModal from "../SendReviewsModal/SendReviewsModal";

// Hooks
// import useTheme from "../ThemeProvider/useTheme/useTheme";

// Icons
import HomeIcon from "../../icons/HomeIcon";
import CalIcon from "../../icons/CalIcon";
import CardIcon from "../../icons/CardIcon";
import UserIcon from "../../icons/UserIcon";
import HelpIcon from "../../icons/HelpIcon";
import MorningIcon from "../../icons/MorningIcon";
import MiddayIcon from "../../icons/MiddayIcon";
import EveningIcon from "../../icons/EveningIcon";
import MessagesIcon from "../../icons/MessagesIcon";
// import NoonIcon from "../../icons/NoonIcon";
import ArrowLeftIcon from "../../icons/ArrowLeftIcon";
import AccountSettings from "./AccountSettings/AccountSettings";
import BottomNavigation from "../BottomNavigation/BottomNavigation";

// Utils
import { getMomentWithTz } from "../../utils/date";

// Images
import GenericUserProfilePhoto from "../ProfilePicture/DefaultProfilePic";

const NavMenuLink = ({ to, title, icon }) => {
  return (
    <NavLink
      to={to}
      // exact={true}
      isActive={(match, location) => {
        if (!match) {
          return false;
        }

        if (match.url === "/account" && !match.isExact) {
          return false;
        }

        return true;
      }}
      className={(isActive) =>
        `tbk-relative tbk-flex tbk-py-0.5${
          isActive
            ? " after:tbk-absolute after:tbk-top-0 after:tbk--left-2 after:tbk-block after:tbk-h-4 after:tbk-w-1 after:tbk-bg-coral after:tbk-content-['']"
            : ""
        }`
      }
    >
      <div className="tbk-mr-1 tbk-text-primary">{icon}</div>
      <div className="tbk-text-h3-subtitle tbk-text-primary">{title}</div>
    </NavLink>
  );
};

const PageHeadDateLabel = ({ title, label, icon: Icon }) => (
  <div className="tbk-flex tbk-items-center">
    <div className="tbk-mr-3 tbk-hidden md:tbk-block">
      <Icon size={40} />
    </div>
    <div>
      <div className="tbk-text-main tbk-hidden tbk-text-primary md:tbk-block">
        {title}
      </div>
      <div className="tbk-text-main md:tbk-text-h3-subtitle tbk-text-blue-grey md:tbk-text-primary">
        {label}
      </div>
    </div>
  </div>
);

export default function UserProfilePage({
  selectedLanguage,
  onSelectLanguage,
}) {
  const location = useLocation();
  const { region } = useRegion();

  const { user } = useAppState();

  const userFirstName = user && user.firstName;
  const userFamilyName = user && user.lastName;
  const userPhoto = user && user.photo;

  const getGreetingText = useCallback(() => {
    const currentTime = moment();

    const splitAfternoon = 12; // 24hr time to split the afternoon
    const splitEvening = 17; // 24hr time to split the evening
    const currentHour = parseFloat(currentTime.format("HH"));

    if (currentHour >= splitAfternoon && currentHour <= splitEvening) {
      // Between 12 PM and 5PM
      return "Good afternoon";
    } else if (currentHour >= splitEvening) {
      // Between 5PM and Midnight
      return "Good evening";
    }

    // Between dawn and noon
    return "Good morning";
  }, []);

  const greetingText = getGreetingText();

  const pageHeadDateLabel = getMomentWithTz(new Date(), region).format(
    "MMMM D, dddd"
  );

  return (
    <>
      {/*<Title>Personal Training in London | TruBe</Title>*/}
      <Meta
        name="description"
        content={`On-demand, in person, personal training in ${
          region === "US" ? "Los Angeles" : "London"
        }.`}
      />
      <Header showLinks={false} />

      <div className="tbk-bg-grey-light tbk-pb-7 lg:tbk-pb-24">
        <div className="tbk-container tbk-mx-auto tbk-px-2 tbk-pt-4 tbk-pb-24 lg:tbk-px-2 lg:tbk-pb-24">
          <div className="lg:tbk-grid lg:tbk-grid-cols-account lg:tbk-items-start lg:tbk-gap-5">
            <div className="tbk-sticky tbk-top-4 tbk-hidden tbk-rounded-lg tbk-border tbk-border-solid tbk-border-blue-grey-light tbk-bg-basic-white lg:tbk-block">
              <div className="tbk-px-3 tbk-pt-4 tbk-text-center">
                <div className="tbk-mb-3 tbk-inline-block">
                  {userPhoto ? (
                    <img
                      className="tb-profile-picture-img img-responsive tbk-h-7 tbk-w-7 tbk-rounded-full tbk-object-cover"
                      itemProp="image"
                      src={userPhoto}
                      alt={`${userFirstName} ${userFamilyName} photo`}
                    />
                  ) : (
                    <GenericUserProfilePhoto size={64} />
                  )}
                </div>
                <hr className="tbk-border-blue-grey-light" />
              </div>

              <div className="tbk-px-2 tbk-pt-3 tbk-pb-4">
                <ul className="tbk-mb-5">
                  <li className="tbk-mb-3">
                    <NavMenuLink
                      to={"/account"}
                      active={true}
                      icon={<HomeIcon />}
                      title="Home"
                    />
                  </li>
                  <li className="tbk-mb-3">
                    <NavMenuLink
                      to={"/account/bookings"}
                      active={false}
                      icon={<CalIcon />}
                      title="Bookings"
                    />
                  </li>
                  <li className="tbk-mb-3">
                    <NavMenuLink
                      to={"/account/messages"}
                      active={false}
                      icon={<MessagesIcon />}
                      title="Messages"
                    />
                  </li>
                  {/*<li className="tbk-mb-3">*/}
                  {/*  <NavMenuLink*/}
                  {/*    to={"/account/payments"}*/}
                  {/*    active={false}*/}
                  {/*    icon={<CardIcon />}*/}
                  {/*    title="Payments"*/}
                  {/*  />*/}
                  {/*</li>*/}
                  <li className="tbk-mb-3">
                    <NavMenuLink
                      to={"/account/settings"}
                      active={false}
                      icon={<UserIcon />}
                      title="Account"
                    />
                  </li>
                </ul>
                <Link to="/contact" className="tbk-flex">
                  <div className="tbk-mr-1 tbk-text-blue-grey">
                    <HelpIcon />
                  </div>
                  <div className="tbk-text-h3-subtitle tbk-text-blue-grey">
                    Help
                  </div>
                </Link>
              </div>
            </div>

            <div className="tbk-min-w-0">
              <Switch>
                <Route exact path="/account/">
                  <div className="md:tbk-pb-2 lg:tbk-mt-3 lg:tbk-flex lg:tbk-justify-between">
                    <div>
                      <div className="tbk-flex">
                        <div className="tbk-mr-2 tbk-inline-block lg:tbk-hidden">
                          {userPhoto ? (
                            <img
                              className="tb-profile-picture-img img-responsive tbk-h-7 tbk-w-7 tbk-rounded-full tbk-object-cover"
                              itemProp="image"
                              src={userPhoto}
                              alt={`${userFirstName} ${userFamilyName} photo`}
                            />
                          ) : (
                            <GenericUserProfilePhoto size={64} />
                          )}
                        </div>
                        <div>
                          <div className="tbk-text-main lg:tbk-text-title-medium tbk-text-primary">
                            {greetingText},
                          </div>
                          <div className="tbk-text-title-bold md:tbk-text-h2-black tbk-mb-2 tbk-text-primary lg:tbk-mb-0">
                            {userFirstName} {userFamilyName}
                          </div>
                        </div>
                      </div>
                    </div>

                    <PageHeadDateLabel
                      title={"Today"}
                      label={pageHeadDateLabel}
                      icon={
                        greetingText === "Good morning"
                          ? MorningIcon
                          : greetingText === "Good afternoon"
                          ? MiddayIcon
                          : EveningIcon
                      }
                    />
                  </div>

                  <hr className="tbk-border-blue-grey-light" />

                  <Home />
                </Route>
                <Route exact path="/account/bookings">
                  <div className="md:tbk-pb-2 lg:tbk-mt-3 lg:tbk-flex lg:tbk-justify-between">
                    <div>
                      <div className="tbk-text-main lg:tbk-text-title-medium tbk-text-primary">
                        The list of all of your
                      </div>
                      <div className="tbk-text-title-bold md:tbk-text-h2-black tbk-mb-2 tbk-text-primary lg:tbk-mb-0">
                        Bookings
                      </div>
                    </div>

                    <PageHeadDateLabel
                      title={"Today"}
                      label={pageHeadDateLabel}
                      icon={
                        greetingText === "Good morning"
                          ? MorningIcon
                          : greetingText === "Good afternoon"
                          ? MiddayIcon
                          : EveningIcon
                      }
                    />
                  </div>

                  <hr className="tbk-border-blue-grey-light" />

                  <Bookings />
                </Route>
                <Route path="/account/bookings/:BookingId">
                  <div className="md:tbk-pb-2 lg:tbk-mt-3 lg:tbk-flex lg:tbk-justify-between">
                    <div>
                      <div className="tbk-text-main lg:tbk-text-title-medium tbk-text-primary">
                        Here is your
                      </div>
                      <div className="tbk-text-title-bold md:tbk-text-h2-black tbk-mb-2 tbk-text-primary lg:tbk-mb-0">
                        Session details
                      </div>
                    </div>

                    <PageHeadDateLabel
                      title={"Today"}
                      label={pageHeadDateLabel}
                      icon={
                        greetingText === "Good morning"
                          ? MorningIcon
                          : greetingText === "Good afternoon"
                          ? MiddayIcon
                          : EveningIcon
                      }
                    />
                  </div>

                  <hr className="tbk-border-blue-grey-light" />

                  <div className="tbk-mb-3 tbk-hidden md:tbk-flex">
                    <Link
                      to="/account/bookings/"
                      className="tbk-text-main-linked tbk-flex tbk-items-center tbk-text-blue hover:tbk-text-blue-light"
                    >
                      <div className="tbk-mr-1 tbk-text-blue">
                        <ArrowLeftIcon />
                      </div>
                      <span>Back</span>
                    </Link>
                  </div>
                  <SessionDetails />
                </Route>
                <Route exact path="/account/payments">
                  <div className="md:tbk-pb-2 lg:tbk-mt-3 lg:tbk-flex lg:tbk-justify-between">
                    <div>
                      <div className="tbk-text-main lg:tbk-text-title-medium tbk-text-primary">
                        Here you may manage your
                      </div>
                      <div className="tbk-text-title-bold md:tbk-text-h2-black tbk-mb-2 tbk-text-primary lg:tbk-mb-0">
                        Payments
                      </div>
                    </div>

                    <PageHeadDateLabel
                      title={"Today"}
                      label={pageHeadDateLabel}
                      icon={
                        greetingText === "Good morning"
                          ? MorningIcon
                          : greetingText === "Good afternoon"
                          ? MiddayIcon
                          : EveningIcon
                      }
                    />
                  </div>

                  <hr className="tbk-border-blue-grey-light" />

                  <Payments />
                </Route>

                <Route exact path="/account/packages">
                  <div className="md:tbk-pb-2 lg:tbk-mt-3 lg:tbk-flex lg:tbk-justify-between">
                    <div>
                      <div className="tbk-text-main lg:tbk-text-title-medium tbk-text-primary">
                        The list of
                      </div>
                      <div className="tbk-text-title-bold md:tbk-text-h2-black tbk-mb-2 tbk-text-primary lg:tbk-mb-0">
                        Your Packages
                      </div>
                    </div>

                    <PageHeadDateLabel
                      title={"Today"}
                      label={pageHeadDateLabel}
                      icon={
                        greetingText === "Good morning"
                          ? MorningIcon
                          : greetingText === "Good afternoon"
                          ? MiddayIcon
                          : EveningIcon
                      }
                    />
                  </div>

                  <hr className="tbk-border-blue-grey-light" />

                  <div className="tbk-mb-3 tbk-hidden md:tbk-flex">
                    <Link
                      to="/account"
                      className="tbk-text-main-linked tbk-flex tbk-items-center tbk-text-blue hover:tbk-text-blue-light"
                    >
                      <div className="tbk-mr-1 tbk-text-blue">
                        <ArrowLeftIcon />
                      </div>
                      <span>Back</span>
                    </Link>
                  </div>

                  <Packages />
                </Route>

                <Route path="/account/messages">
                  <div className="md:tbk-pb-2 lg:tbk-mt-3 lg:tbk-flex lg:tbk-justify-between">
                    <div>
                      <div className="tbk-text-main lg:tbk-text-title-medium tbk-text-primary">
                        Here are your
                      </div>
                      <div className="tbk-text-title-bold md:tbk-text-h2-black tbk-mb-2 tbk-text-primary lg:tbk-mb-0">
                        Messages
                      </div>
                    </div>

                    <PageHeadDateLabel
                      title={"Today"}
                      label={pageHeadDateLabel}
                      icon={
                        greetingText === "Good morning"
                          ? MorningIcon
                          : greetingText === "Good afternoon"
                          ? MiddayIcon
                          : EveningIcon
                      }
                    />
                  </div>

                  <hr className="tbk-hidden tbk-border-blue-grey-light lg:tbk-block" />

                  {/*<div className="tbk-mb-3 tbk-hidden md:tbk-flex">
                    <Link
                      to="/account"
                      className="tbk-text-main-linked tbk-flex tbk-items-center tbk-text-blue hover:tbk-text-blue-light"
                    >
                      <div className="tbk-mr-1 tbk-text-blue">
                        <ArrowLeftIcon />
                      </div>
                      <span>Back</span>
                    </Link>
                  </div>*/}

                  <Messages />
                </Route>

                <Route exact path="/account/settings">
                  <div className="md:tbk-pb-2 lg:tbk-mt-3 lg:tbk-flex lg:tbk-justify-between">
                    <div>
                      <div className="tbk-text-main lg:tbk-text-title-medium tbk-text-primary">
                        Manage you personal data in
                      </div>
                      <div className="tbk-text-title-bold md:tbk-text-h2-black tbk-mb-2 tbk-text-primary lg:tbk-mb-0">
                        Account Settings
                      </div>
                    </div>

                    <PageHeadDateLabel
                      title={"Today"}
                      label={pageHeadDateLabel}
                      icon={
                        greetingText === "Good morning"
                          ? MorningIcon
                          : greetingText === "Good afternoon"
                          ? MiddayIcon
                          : EveningIcon
                      }
                    />
                  </div>

                  <hr className="tbk-border-blue-grey-light" />
                  <AccountSettings
                    selectedLanguage={selectedLanguage}
                    onSelectLanguage={onSelectLanguage}
                  />
                </Route>
              </Switch>
            </div>
          </div>
        </div>
        <div className="lg:tbk-hidden">
          <BottomNavigation />
        </div>
      </div>
      <Switch>
        <Route exact path="/account/">
          <Footer />
        </Route>
        <Route path="/account/bookings">
          <Footer />
        </Route>
        <Route exact path="/account/packages">
          <Footer />
        </Route>
        <Route path="/account/messages">
          <div className="tbk-hidden lg:tbk-block">
            <Footer />
          </div>
        </Route>
        <Route exact path="/account/settings">
          <Footer />
        </Route>
      </Switch>

      {!location.pathname.includes("repeat") ? (
        <SendReviewsModal onClose={() => {}} />
      ) : null}
    </>
  );
}
